import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Icons } from "../../constants/icon-library";
import { editToolStatus, getTools } from "../../features/tools/toolsSlice";
import NotFoundMessage from "../../components/common/Modal/NotFoundMessage";
import ToolTypeModal from "./ToolTypeModal";
import { toast } from "react-toastify";
import { createToolFromImage } from "../../features/tools/toolsAPI";

const MAX_DESCRIPTION_LENGTH = 100;

const getTruncatedDescription = (text = "", limit = MAX_DESCRIPTION_LENGTH) => {
  if (!text) return "";
  return text.length > limit ? `${text.slice(0, limit).trim()}...` : text;
};

function Tools({ assistantId, onAssistantUpdated }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { items: tools = [], loading = false } = useSelector(
    (state) => state.tools || {},
  );
  const [toolStatusMap, setToolStatusMap] = useState({});
  const [initialStatusMap, setInitialStatusMap] = useState({});
  const [enableAll, setEnableAll] = useState(false);
  const [showToolTypeModal, setShowToolTypeModal] = useState(false);
  const [isCreatingFromImage, setIsCreatingFromImage] = useState(false);

  useEffect(() => {
    if (assistantId) {
      dispatch(getTools(assistantId));
    }
  }, [assistantId, dispatch]);

  useEffect(() => {
    if (tools.length) {
      const map = {};
      tools.forEach((tool) => {
        map[tool.uuid] = !!tool.isEnabled;
      });
      setToolStatusMap(map);
      setInitialStatusMap(map);
      setEnableAll(Object.values(map).every(Boolean));
    }
  }, [tools]);

  const showToolsNotFoundError = !loading && tools.length === 0;

  const hasChanges =
    JSON.stringify(toolStatusMap) !== JSON.stringify(initialStatusMap);

  const handleToggleTool = (uuid) => {
    setToolStatusMap((prev) => ({
      ...prev,
      [uuid]: !prev[uuid],
    }));
  };

  const handleToggleAll = () => {
    const newValue = !enableAll;
    const updated = {};
    Object.keys(toolStatusMap).forEach((key) => {
      updated[key] = newValue;
    });
    setToolStatusMap(updated);
    setEnableAll(newValue);
  };

  const handleSaveChanges = async () => {
    try {
      const payload = Object.entries(toolStatusMap).map(
        ([uuid, isEnabled]) => ({
          uuid,
          isEnabled,
        }),
      );

      await dispatch(editToolStatus(payload)).unwrap();
      onAssistantUpdated?.();
      setInitialStatusMap(toolStatusMap);

      toast.success("Tool settings saved successfully");
    } catch (error) {
      toast.error("Failed to save tool settings");
    }
  };

  const handleCreateFromImage = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;

    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (!files || files.length === 0) {
        toast.error("No file selected");
        return;
      }

      if (files.length > 5) {
        toast.error("You can only select up to 5 files at once");
        return;
      }

      if (isCreatingFromImage) {
        toast.warning("Please wait for the current upload to complete");
        return;
      }
      const loadingToastId = toast.loading("Creating tool...");
      setIsCreatingFromImage(true);
      setShowToolTypeModal(false);

      try {
        const result = await createToolFromImage(assistantId, files);

        toast.dismiss(loadingToastId);

        if (!result?.success) {
          toast.error(result?.message || "Failed to create tool from image");
          setIsCreatingFromImage(false);
          return;
        }

        const tool = result?.data;
        toast.success("Tool created successfully!");
        navigate(`tool/${tool?.toolId}/edit`);
      } catch (error) {
        console.error("Error creating tool from image:", error);
        toast.dismiss(loadingToastId);
        toast.error("Failed to create tool from image");
        setIsCreatingFromImage(false);
      }
    };

    input.click();
  };

  const handleCreateCustomTool = () => {
    setShowToolTypeModal(false);
    navigate(`tool`);
  };

  return (
    <div>
      <div className="row">
        <div className="card border p-0">
          <div className="card-header p-4 py-3 border-bottom bg-body d-flex justify-content-between align-items-center">
            <h3 className="mb-0">Tools</h3>
            {!showToolsNotFoundError && (
              <div className="d-flex align-items-center gap-3">
                <div className="form-check form-switch mb-0">
                  <input
                    className={`form-check-input ${
                      enableAll
                        ? "bg-success border-success"
                        : "bg-danger border-danger"
                    }`}
                    type="checkbox"
                    checked={enableAll}
                    onChange={handleToggleAll}
                    style={{ cursor: "pointer" }}
                  />
                  <label className="form-check-label">Enable All</label>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={() => setShowToolTypeModal(true)}
                >
                  <i
                    className={`wb ${Icons("add")} fs-8 me-2 align-middle`}
                  ></i>
                  Add Tool
                </button>
              </div>
            )}
          </div>

          <div className="card-body">
            <div className="col-12">
              {showToolsNotFoundError ? (
                <NotFoundMessage
                  message="No Tools found."
                  onAddNew={() => setShowToolTypeModal(true)}
                  buttonMessage="Add Tool"
                />
              ) : (
                <div className="row">
                  {[...tools].reverse().map((tool) => (
                    <div key={tool.uuid} className="col-lg-4 col-md-6 mb-4">
                      <div className="card h-100 shadow-sm border">
                        <div className="card-body d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <h5 className="card-title mb-0 text-truncate me-2">
                              {tool.name}
                            </h5>

                            <div className="form-check form-switch">
                              <input
                                className={`form-check-input ${
                                  toolStatusMap[tool.uuid]
                                    ? "bg-success border-success"
                                    : "bg-danger border-danger"
                                }`}
                                type="checkbox"
                                checked={toolStatusMap[tool.uuid] || false}
                                onChange={() => handleToggleTool(tool.uuid)}
                                style={{ cursor: "pointer" }}
                              />
                              <label className="form-check-label">
                                {toolStatusMap[tool.uuid]
                                  ? "Enable"
                                  : "Disable"}
                              </label>
                            </div>
                          </div>

                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center text-muted mb-2">
                              <i
                                className={`wb ${Icons("clock")} fs-8 me-2`}
                              ></i>
                              <small>
                                {moment
                                  .utc(tool.createdAt)
                                  .format("MMM D, YYYY • h:mm A")}
                              </small>
                            </div>

                            <div className="d-flex align-items-center text-muted">
                              <i className={`wb ${Icons("key")} fs-8 me-2`}></i>
                              <small>{tool.key || "—"}</small>
                            </div>
                            <div className="d-flex align-items-center text-muted">
                              <i
                                className={`wb ${Icons(
                                  "description",
                                )} fs-8 me-2`}
                              ></i>
                              <small>
                                {getTruncatedDescription(tool.description) ||
                                  "—"}
                              </small>
                            </div>
                          </div>

                          <div className="d-flex justify-content-end mt-auto">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => navigate(`tool/${tool.uuid}/edit`)}
                            >
                              <i
                                className={`wb ${Icons(
                                  "form",
                                )} fs-8 me-1 align-middle`}
                              ></i>
                              Edit
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {hasChanges && (
              <div
                className="position-sticky bottom-0"
                style={{
                  zIndex: 3,
                  left: 0,
                  right: 0,
                  marginLeft: "-1.5rem",
                  marginRight: "-1.5rem",
                  marginBottom: "-1.5rem",
                }}
              >
                <div
                  className="d-flex justify-content-end bg-body border-top"
                  style={{
                    width: "100%",
                    padding: "12px 24px",
                    borderTop: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveChanges}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ToolTypeModal
        show={showToolTypeModal}
        onClose={() => setShowToolTypeModal(false)}
        onCreateFromImage={handleCreateFromImage}
        onCreateCustomTool={handleCreateCustomTool}
      />
    </div>
  );
}

export default Tools;
